import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import styled from 'styled-components';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import { Card, CardBody } from '@spark/ui-components/components/Card';
import { DatePickerField } from '@spark/ui-components/components/form/date-pickers/DatePicker';
import SearchSelect from '@spark/ui-components/components/form/SearchSelect';
import Icon from '@spark/ui-components/components/Icon';
import { dashboardLojaOptions } from '../../shared/lojaOptions';

const onlyDigits = (value, maxLength) => String(value || '').replace(/\D/g, '').slice(0, maxLength);

const formatDateInputValue = (date) => {
  if (!date) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const DashboardFilters = ({
  values,
  loading,
  onApply,
  onClear
}) => {
  const [localValues, setLocalValues] = useState(values);

  useEffect(() => {
    setLocalValues(values);
  }, [values]);

  const handleChange = (field, value) => {
    setLocalValues((prev) => ({
      ...prev,
      [field]: field === 'CNPJCliente' ? onlyDigits(value, 14) : value
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onApply(localValues);
  };

  return (
    <Card>
      <CardBody>
        <Form onSubmit={handleSubmit}>
          <FilterGrid>
            <FilterField>
              <Form.Group className="dashboard-date-picker">
                <DatePickerField
                  label="Data inicial"
                  value={localValues.DataInicial || ''}
                  onChange={(date) => handleChange('DataInicial', formatDateInputValue(date))}
                  inputClassName="dashboard-date-input"
                  allowClear={false}
                  required
                />
              </Form.Group>
            </FilterField>
            <FilterField>
              <Form.Group className="dashboard-date-picker">
                <DatePickerField
                  label="Data final"
                  value={localValues.DataFinal || ''}
                  onChange={(date) => handleChange('DataFinal', formatDateInputValue(date))}
                  inputClassName="dashboard-date-input"
                  allowClear={false}
                  required
                />
              </Form.Group>
            </FilterField>
            <FilterField $wide>
              <div className="dashboard-loja-select">
                <SearchSelect
                  name="Loja"
                  label="Loja"
                  value={localValues.Loja || []}
                  onChange={(selected) => handleChange('Loja', selected || [])}
                  options={dashboardLojaOptions}
                  placeholder="Selecione as lojas"
                  isMulti
                  showSelectAll
                />
              </div>
            </FilterField>
            <FilterField>
              <Form.Group className="dashboard-text-field">
                <Form.Label>CNPJ Cliente</Form.Label>
                <Form.Control
                  value={localValues.CNPJCliente || ''}
                  onChange={(event) => handleChange('CNPJCliente', event.target.value)}
                  placeholder="Digite o CNPJ"
                />
              </Form.Group>
            </FilterField>
            <Actions>
              <ButtonWithLoading type="submit" variant="primary" loading={loading} loadingText="Aplicando...">
                <Icon name="search" size={14} />
                <span>Aplicar</span>
              </ButtonWithLoading>
              <Button
                type="button"
                variant="secondary"
                onClick={onClear}
                disabled={loading}
              >
                <Icon name="refresh" size={14} />
                <span>Limpar</span>
              </Button>
            </Actions>
          </FilterGrid>
        </Form>
      </CardBody>
    </Card>
  );
};

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(160px, 1fr) minmax(160px, 1fr) minmax(280px, 2.2fr) minmax(190px, 1.25fr) auto;
  gap: 16px 24px;
  align-items: end;

  .form-group {
    margin-bottom: 0;
  }

  label,
  .dashboard-date-picker > div:first-child,
  .dashboard-loja-select > div > span:first-child {
    display: block;
    min-height: 16px;
    line-height: 16px;
    font-size: 12px;
    font-weight: 500;
    color: #4b5563;
    margin-bottom: 6px;
  }

  .dashboard-text-field label {
    margin-bottom: 6px;
  }

  .form-control {
    height: 40px;
    border-radius: 0;
    font-size: 14px;
  }

  .dashboard-date-picker {
    > div:first-child {
      margin-bottom: 6px !important;
    }

    .react-datepicker-wrapper,
    .react-datepicker__input-container {
      width: 100%;
    }

    .dashboard-date-input {
      width: 100%;
      height: 40px;
      padding: 0.375rem 0.75rem;
      border: 1px solid #ced4da;
      border-radius: 0;
      font-size: 14px;
      color: #495057;
    }
  }

  .dashboard-loja-select {
    > div {
      margin-bottom: 0;
    }

    > div > span:first-child {
      margin-bottom: 6px;
    }

    > div > div {
      height: 40px;
    }

    .react-select__control {
      min-height: 40px;
      height: 40px;
    }

    .react-select__value-container,
    .react-select__input,
    .react-select__indicators {
      min-height: 38px;
      height: 38px;
    }
  }

  @media (max-width: 1199px) {
    grid-template-columns: repeat(2, minmax(220px, 1fr));
  }

  @media (max-width: 575px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const FilterField = styled.div`
  min-width: 0;

  ${({ $wide }) => $wide && `
    min-width: 280px;
  `}

  @media (max-width: 1199px) {
    min-width: 0;

    ${({ $wide }) => $wide && `
      grid-column: 1 / -1;
    `}
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  height: 40px;
  align-self: end;

  button {
    min-width: 92px;
    height: 40px;
  }

  @media (max-width: 991px) {
    justify-content: flex-start;
  }

  @media (max-width: 575px) {
    height: auto;
    flex-direction: column;
    align-items: stretch;

    button {
      width: 100%;
    }
  }
`;

export default DashboardFilters;
